import React from 'react';
import './invoice-search-result.scss';
import { Text, View } from '../../common';
import {
  getSearchKey,
  InvoiceSearchParameter,
  useAppSelector,
} from '@zonofi/zono-money-store';
import { Avatar, LoadingSpinner } from '../../components';

interface InvoiceSearchResultProps {
  searchResults: any[];
  searchParameter?: InvoiceSearchParameter;
  onUpdateSearchParameter?: (
    paramId: string,
    value: string,
    updateSearchValue?: string | string[]
  ) => void;
  setShowSearchWindow?: (val: boolean) => void;
  isLoading: boolean;
}

export const InvoiceSearchResult: React.FC<InvoiceSearchResultProps> = ({
  searchResults,
  searchParameter = undefined,
  onUpdateSearchParameter = undefined,
  setShowSearchWindow = () => {},
  isLoading,
}) => {
  const searchKey = useAppSelector(getSearchKey);
  const { parameterId, value } = searchParameter!;

  const highlightMatchingText = (
    text: string,
    searchValue: string
  ): React.ReactNode => {
    if (!text || !searchValue) return text;

    // Create a case-insensitive regex to find matches
    const regex = new RegExp(`(${searchValue})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) => {
      // Check if this part matches the search value (case-insensitive)
      const isMatch = part.toLowerCase() === searchValue.toLowerCase();

      return isMatch ? (
        <Text
          key={index}
          className="invoice-search-result-customers-customer-info-name font-bold"
        >
          {part}
        </Text>
      ) : (
        <Text className="invoice-search-result-customers-customer-info-name font-regular">
          {part}
        </Text>
      );
    });
  };

  const renderCustomers = () => {
    return (
      <View className="invoice-search-result-wrapper">
        <View className="invoice-search-result-customers">
          {searchResults.map((customer, idx, customersArr) => (
            <>
              <View
                className="invoice-search-result-customers-customer"
                key={customer?.id}
                onPress={() => {
                  onUpdateSearchParameter?.(
                    parameterId,
                    searchKey,
                    customer?.id
                  );
                  setShowSearchWindow?.(false);
                }}
              >
                <Avatar title={customer?.name} size={28} />
                <View className="invoice-search-result-customers-customer-info">
                  <Text className="invoice-search-result-customers-customer-info-name font-regular">
                    {highlightMatchingText(customer?.name, searchKey)}
                  </Text>
                  <Text className="invoice-search-result-customers-customer-info-mobile font-regular">
                    {customer?.mobile?.slice(2)}
                  </Text>
                </View>
              </View>
              {customersArr?.length - 1 > idx && (
                <View className="invoice-search-result-customers-divider" />
              )}
            </>
          ))}
        </View>
      </View>
    );
  };

  const renderSearchResults = () => {
    switch (parameterId) {
      case 'customerId':
        return renderCustomers();
      default:
        return null;
    }
  };

  return (
    <View className="invoice-search-result">
      <Text className="invoice-search-result-label font-regular">
        {searchResults?.length} result{searchResults?.length > 1 ? 's' : ''}{' '}
        found for "<Text className="font-bold">{value}</Text>"
      </Text>
      {isLoading ? <LoadingSpinner size={'small'} /> : renderSearchResults()}
    </View>
  );
};
